import { SelectOption } from '@common/types/common';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
    alpha,
    Chip,
    FormControl,
    IconButton,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Select,
    styled,
    TextField,
    TextFieldProps,
} from '@mui/material';
import { capitalizeFirstLetter } from '@utils/common';
import clsx from 'clsx';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

type SearchProps<TSearchBy extends string> = TextFieldProps & {
    searchByOptions?: SelectOption<TSearchBy>[];
    searchByState?: [TSearchBy, React.Dispatch<React.SetStateAction<TSearchBy>> | undefined];
    clearable?: boolean;
    onClear?: () => void;
};

const Search = <TSearchBy extends string>({
    searchByOptions,
    searchByState,
    clearable,
    onClear,
    ...props
}: SearchProps<TSearchBy>) => {
    const [open, setOpen] = useState(false);
    const [searchBy, setSearchBy] = searchByState ?? [];

    const inputSlotProps = (props.slotProps?.input as TextFieldProps | undefined) ?? {};

    return !searchByState ? (
        <TextField
            variant="outlined"
            size="small"
            {...props}
            slotProps={{
                ...props.slotProps,
                input: {
                    sx: {
                        borderRadius: '8px',
                        ...(inputSlotProps.sx ?? {}),
                    },
                    className: clsx('shadow-2', inputSlotProps.className),
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    ...(clearable && props.value
                        ? {
                              endAdornment: (
                                  <InputAdornment position="end">
                                      <IconButton
                                          onClick={() => {
                                              props.onChange?.({
                                                  target: { value: '' },
                                              } as React.ChangeEvent<HTMLInputElement>);
                                              onClear?.();
                                          }}
                                          size="small"
                                      >
                                          <ClearIcon />
                                      </IconButton>
                                  </InputAdornment>
                              ),
                          }
                        : {}),
                    ...props.InputProps,
                    ...props.slotProps?.input,
                },
            }}
        />
    ) : (
        <>
            <FormControl className="min-w-[200px]" size="small">
                <Select
                    className={clsx('bg-primary-600 rounded-none rounded-l-lg border-primary-600', {
                        'rounded-b-none': open,
                    })}
                    displayEmpty
                    classes={{
                        icon: 'fill-white',
                    }}
                    value={searchBy}
                    onChange={(e) => setSearchBy?.(e.target.value as TSearchBy)}
                    input={
                        <OutlinedInput
                            notched
                            classes={{ notchedOutline: 'border-primary-600', input: 'text-white' }}
                        />
                    }
                    renderValue={(value) =>
                        `Search by: ${capitalizeFirstLetter(
                            searchByOptions?.find((x) => x.value === value)?.label ?? ''
                        )}`
                    }
                    size="small"
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    MenuProps={{
                        slotProps: {
                            paper: {
                                className: 'rounded-none rounded-b-lg',
                            },
                        },
                        classes: {
                            list: 'py-0',
                        },
                    }}
                >
                    {searchByOptions?.map((option) => (
                        <MenuItem key={`search-option-${option.value}`} value={option.value} className="px-4 py-0">
                            <div className="border-b-[1px] w-full py-[6px]">{option.label}</div>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                size="small"
                variant="outlined"
                {...props}
                slotProps={{
                    ...props.slotProps,
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        ...props.InputProps,
                        ...props.slotProps?.input,
                        className: 'rounded-none rounded-r-lg',
                    },
                }}
            />
        </>
    );
};

const PublicSearch = styled(
    ({
        scope,
        ...props
    }: TextFieldProps & { scope?: { label?: string; onDelete?: () => void }; onClear?: () => void }) => (
        <Search
            clearable
            {...props}
            slotProps={{
                ...props.slotProps,
                input: {
                    ...props.slotProps?.input,
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="black" />
                            {scope?.label && (
                                <Chip
                                    variant="outlined"
                                    className="ml-2"
                                    label={
                                        <FormattedMessage
                                            id="search.scope"
                                            defaultMessage={`In: {searchScope}`}
                                            values={{ searchScope: scope.label }}
                                        />
                                    }
                                    size="small"
                                    color="victron-blue"
                                    onMouseDown={(e) => {
                                        // Keep the text input focused when interacting with the chip.
                                        e.preventDefault();
                                    }}
                                    onDelete={scope?.onDelete}
                                />
                            )}
                        </InputAdornment>
                    ),
                },
            }}
        />
    )
)(() => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 32,
        backgroundColor: alpha('#000', 0.04),
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha('#000', 0.56),
    },
}));

export type { SearchProps };

const SearchNamespace = Object.assign(Search, {
    Default: Search,
    Public: PublicSearch,
});

export { SearchNamespace as Search };
